/**
 * Lightweight Markdown renderer — no external dependencies.
 *
 * Supports: headings, bold, italic, inline code, code blocks,
 * links, unordered/ordered lists, blockquotes, horizontal rules, tables.
 *
 * Workspace link support: links containing "?workspace=" are intercepted
 * and rendered as WorkspaceLink components that trigger SPA navigation
 * instead of opening a new tab.
 *
 * Event Hub link support: a link to this portal's own /event pages (the
 * agent's nomination page and nomination list links) opens in this tab, next
 * to the chat, rather than in a second portal tab.
 */

import { useTranslation } from 'react-i18next';
import { useState, useCallback, type ReactNode } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useChatPanel } from '../../context/ChatPanelContext';
import { hasEventHubAccess } from '../../eventHub/utils/authorization';
import { useOpenWorkspaceFile, workspacePathFromUrl } from '../../hooks/useOpenWorkspaceFile';

interface MarkdownRendererProps {
  markdown: string;
}

// ── Workspace link component (SPA navigation) ──────────────────────────────

function WorkspaceLink({ filePath, children }: { filePath: string; children: React.ReactNode }) {
  const { t } = useTranslation('chat');
  const openWorkspaceFile = useOpenWorkspaceFile();

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    openWorkspaceFile(filePath);
  }, [filePath, openWorkspaceFile]);

  return (
    <a href="#" className="md-link md-workspace-link" onClick={handleClick} title={t('files.openInWorkspace')}>
      {children}
    </a>
  );
}

// ── Event Hub link component (SPA navigation) ───────────────────────────────

/**
 * The in-app path (with query and hash) of a link to this portal's own Event Hub
 * pages, or null when the URL is anything else. Same origin only: the agent links
 * to its stage's portal, which from another host (another stage, localhost) is a
 * different app and keeps opening in a new tab.
 */
function eventHubPathFromUrl(url: string): string | null {
  try {
    const u = new URL(url, window.location.href);
    if (u.origin !== window.location.origin) return null;
    if (u.pathname !== '/event' && !u.pathname.startsWith('/event/')) return null;
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }
}

/**
 * Only a plain left click is taken over, as in WorkspaceFileLink: Cmd/Ctrl/Shift/
 * middle click keep the browser's new-tab behaviour, and the href stays the real
 * URL so it can be copied. It also keeps the new tab for a user without Event Hub
 * access, whose /event page is a denial with no chat beside it.
 *
 * From an Event Hub form the link opens here too, so one click behaves the same
 * everywhere, and unsaved input is left behind. The nomination form starts blank for
 * each event, so a guest typed for one event is never carried to the next.
 */
function RoutedEventHubLink({ href, path, children }: { href: string; path: string; children: ReactNode }) {
  const navigate = useNavigate();
  const { ldap_groups } = useAuth();
  const { exitFullscreen } = useChatPanel();
  const inTab = hasEventHubAccess(ldap_groups);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="md-link"
      onClick={(e) => {
        if (!inTab) return;
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        // The page can't be seen behind a fullscreen chat.
        exitFullscreen();
        navigate(path);
      }}
    >
      {children}
    </a>
  );
}

/** Outside a Router (a component rendered on its own) the link stays a plain new-tab one. */
function EventHubLink({ href, path, children }: { href: string; path: string; children: ReactNode }) {
  if (!useInRouterContext()) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="md-link">
        {children}
      </a>
    );
  }
  return <RoutedEventHubLink href={href} path={path}>{children}</RoutedEventHubLink>;
}

// ── Code block with copy button ─────────────────────────────────────────────

function CodeBlock({ language, code }: { language: string; code: string }) {
  const { t } = useTranslation('chat');
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [code]);

  return (
    <div className="md-code-block">
      <div className="md-code-header">
        <span className="md-code-lang">{language || t('markdown.code')}</span>
        <button className="md-code-copy" onClick={handleCopy}>
          {copied ? `✓ ${t('markdown.copied')}` : t('markdown.copy')}
        </button>
      </div>
      <pre className="md-code-body"><code>{code}</code></pre>
    </div>
  );
}

// ── Inline markdown parsing ─────────────────────────────────────────────────

function parseInline(text: string): (string | JSX.Element)[] {
  const result: (string | JSX.Element)[] = [];
  // Pattern: **bold**, *italic*, `code`, [text](url), bare URL (https://...)
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\[([^\]]+)\]\(([^)]+)\))|(https?:\/\/[^\s<>)\]]+)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // **bold**
      result.push(<strong key={key++}>{parseInline(match[2])}</strong>);
    } else if (match[3]) {
      // *italic*
      result.push(<em key={key++}>{parseInline(match[4])}</em>);
    } else if (match[5]) {
      // `inline code` — intentionally NOT recursive
      result.push(<code key={key++} className="md-inline-code">{match[6]}</code>);
    } else if (match[7]) {
      // [text](url)
      const url = match[9];
      const safeUrl = /^https?:\/\//i.test(url) ? url : '#';

      // Check if this is a workspace link
      const wsPath = workspacePathFromUrl(url);
      const eventHubPath = safeUrl === '#' ? null : eventHubPathFromUrl(safeUrl);
      if (wsPath) {
        result.push(
          <WorkspaceLink key={key++} filePath={wsPath}>
            {parseInline(match[8])}
          </WorkspaceLink>
        );
      } else if (eventHubPath) {
        result.push(
          <EventHubLink key={key++} href={safeUrl} path={eventHubPath}>
            {parseInline(match[8])}
          </EventHubLink>
        );
      } else {
        result.push(
          <a key={key++} href={safeUrl} target="_blank" rel="noopener noreferrer" className="md-link">
            {parseInline(match[8])}
          </a>
        );
      }
    } else if (match[10]) {
      // Bare URL (https://...) — render as workspace link or normal link
      const bareUrl = match[10];
      const wsPath = workspacePathFromUrl(bareUrl);
      const eventHubPath = eventHubPathFromUrl(bareUrl);
      if (wsPath) {
        result.push(
          <WorkspaceLink key={key++} filePath={wsPath}>
            {bareUrl}
          </WorkspaceLink>
        );
      } else if (eventHubPath) {
        result.push(
          <EventHubLink key={key++} href={bareUrl} path={eventHubPath}>
            {bareUrl}
          </EventHubLink>
        );
      } else {
        result.push(
          <a key={key++} href={bareUrl} target="_blank" rel="noopener noreferrer" className="md-link">
            {bareUrl}
          </a>
        );
      }
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result.length > 0 ? result : [text];
}

// ── Block-level markdown parsing ────────────────────────────────────────────

export default function MarkdownRenderer({ markdown }: MarkdownRendererProps) {
  if (!markdown || !markdown.trim()) return null;

  const lines = markdown.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block: ```lang ... ```
    if (line.trimStart().startsWith('```')) {
      const lang = line.trimStart().slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) {
        i++; // skip closing ``` only if it exists
      }
      elements.push(<CodeBlock key={key++} language={lang} code={codeLines.join('\n')} />);
      continue;
    }

    // Heading: # ## ###
    const headingMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length as 1 | 2 | 3;
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3';
      elements.push(<Tag key={key++} className={`md-h${level}`}>{parseInline(headingMatch[2])}</Tag>);
      i++;
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      elements.push(<hr key={key++} className="md-hr" />);
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <blockquote key={key++} className="md-blockquote">
          {quoteLines.map((ql, qi) => <p key={qi}>{parseInline(ql)}</p>)}
        </blockquote>
      );
      continue;
    }

    // Unordered list: - or *
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      elements.push(
        <ul key={key++} className="md-ul">
          {items.map((item, ii) => <li key={ii}>{parseInline(item)}</li>)}
        </ul>
      );
      continue;
    }

    // Ordered list: 1. 2. etc
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      elements.push(
        <ol key={key++} className="md-ol">
          {items.map((item, ii) => <li key={ii}>{parseInline(item)}</li>)}
        </ol>
      );
      continue;
    }

    // Table: | ... | ... |
    if (line.includes('|') && line.trim().startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      if (tableLines.length >= 2) {
        const parseRow = (row: string) =>
          row.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1).map(c => c.trim());

        const headers = parseRow(tableLines[0]);
        // Skip separator row (index 1)
        const bodyRows = tableLines.slice(2).map(parseRow);

        elements.push(
          <div key={key++} className="md-table-wrap">
            <table className="md-table">
              <thead>
                <tr>{headers.map((h, hi) => <th key={hi}>{parseInline(h)}</th>)}</tr>
              </thead>
              <tbody>
                {bodyRows.map((row, ri) => (
                  <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{parseInline(cell)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
      // tableLines < 2: not a valid table, render consumed lines as paragraphs
      for (const tl of tableLines) {
        elements.push(<p key={key++} className="md-p">{parseInline(tl)}</p>);
      }
      continue; // i is already advanced by the while loop
    }

    // Empty line
    if (!line.trim()) {
      i++;
      continue;
    }

    // Paragraph (default)
    elements.push(<p key={key++} className="md-p">{parseInline(line)}</p>);
    i++;
  }

  return <div className="md-root">{elements}</div>;
}
